import { createElement, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  HIDE_JOBS_INVITE_POPUP,
  INVITE_PROFESSIONALS_TO_JOBS,
  SHOW_MESSAGE,
} from '~/actions';
import { TAB_INDEX } from '~/components/ProfessionalsHub/utils';
import { fireProfessionalSelected, type TProCategory } from '~/posthogs/events';
import { IFavoriteProfessional, useFavoriteProfessionals } from '~/domain/FavoriteProfessionals';
import { IRecentProfessional, useRecentProfessionals } from '~/domain/RecentProfessionals';
import { useRecommendedProfessionals } from '~/domain/RecommendedProfessionals';
import {
  useGetJob,
  useInviteFavoriteToJobMutation,
  useInviteProfessionalsToJobsMutation,
} from '~/domain/Jobs';
import { jobKeys } from '~/domain/Jobs/jobKeys';
import { useIsError, useIsSuccess, useLoading } from '~/hooks';
import { IFavoriteInvite, ProfessionTypeEnum } from '~/models';

const tabToTProCategory: Record<number, TProCategory> = {
  [TAB_INDEX.FAVORITES_LIST]: 'Favorite',
  [TAB_INDEX.RECENT_PROFESSIONALS]: 'Worked With',
  [TAB_INDEX.RECOMMENDED_PROS]: 'New to You',
};

interface IProps {
  jobId?: string;
  jobIds?: string[];
  onClosePopup: () => void;
  isRefetchJob?: boolean;
  defaultProfessionals?: string[];
  isOpen?: boolean;
  filterProfession?: ProfessionTypeEnum;
  officeId?: string;
}

/**
 * @param {boolean} isRefetchJob - Flag to keep the popup loading while job detail query loads.
 * @param {string} jobId - Job ID to send the invitation to. Ignored if `jobIds` is provided.
 * @param {string[]} jobIds - List of job IDs to send invitations to. Takes precedence over `jobId` if defined.
 * @param {function} onClosePopup - Callback function to close the popup.
 * @param {string[]} defaultProfessionals -  default professionals to be selected by default.
 * @param {boolean} isOpen - Flag to determine if the popup is open.
 * @param {ProfessionTypeEnum} filterProfession - Profession type to filter professionals.
 */
export const useInviteProfessional = ({
  isRefetchJob = true,
  jobId,
  jobIds,
  onClosePopup,
  defaultProfessionals,
  isOpen,
  filterProfession,
  officeId,
}: IProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inviteFavoriteToJobMutation = useInviteFavoriteToJobMutation();
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const { data: favoriteProfessionals, isLoading: isLoadingFavoriteProfessionals } =
    useFavoriteProfessionals({
      officeId: officeId || '',
      jobId: jobId,
      enabled: !!isOpen,
    });
  const { data: recentProfessionals, isLoading: isLoadingRecentProfessionals } =
    useRecentProfessionals({
      officeId: officeId || '',
      jobId: jobId,
      enabled: !!isOpen,
    });
  const { data: recommendedProfessionals, isLoading: isLoadingRecommendedProfessionals } =
    useRecommendedProfessionals({
      officeId: officeId || '',
      enabled: !!isOpen,
    });
  const { data: jobDetail, isLoading: isLoadingJobDetail } = useGetJob(jobId, {
    enabled: !!isOpen && !!jobId,
  });

  const isProfessionalAvailable = (isAvailable: boolean | undefined) => {
    return isAvailable ?? true;
  };

  const favoritesListFiltered = useMemo(
    () =>
      (favoriteProfessionals || []).filter((pro: IFavoriteProfessional) =>
        filterProfession
          ? pro.professions?.includes(filterProfession) && isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [favoriteProfessionals, filterProfession],
  );

  const recentsListFiltered = useMemo(
    () =>
      (recentProfessionals || []).filter((pro: IRecentProfessional) =>
        filterProfession
          ? pro.professional?.professions?.includes(filterProfession) &&
            isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [recentProfessionals, filterProfession],
  );

  const recommendedListFiltered = useMemo(
    () =>
      (recommendedProfessionals || []).filter((pro) =>
        filterProfession
          ? pro.professions?.includes(filterProfession) && isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [recommendedProfessionals, filterProfession],
  );

  const { isLoading: isInviteStatusLoading } = useLoading([
    'INVITE_FAVORITE_TO_JOB',
    'INVITE_PROFESSIONALS_TO_JOBS',
  ]);
  const isInviting =
    isInviteStatusLoading ||
    inviteFavoriteToJobMutation.isPending ||
    inviteProfessionalsToJobsMutation.isPending;
  const isLoading =
    isLoadingFavoriteProfessionals ||
    isLoadingRecentProfessionals ||
    (isRefetchJob && isLoadingJobDetail) ||
    isLoadingRecommendedProfessionals;

  const { clearError, isError } = useIsError(['INVITE_FAVORITE_TO_JOB']);
  const { clearSuccess, isSuccess } = useIsSuccess(['INVITE_FAVORITE_TO_JOB']);

  const [activeTab, setActiveTab] = useState(TAB_INDEX.FAVORITES_LIST);
  const [selectedProfessionals, setSelectedProfessionals] = useState<Set<string>>(new Set());
  const sentProfessionalIds = useMemo(() => {
    if (!jobId || jobDetail?.id !== jobId) return new Set<string>();

    return new Set(
      (jobDetail.favoriteInvites ?? []).map(
        (it: IFavoriteInvite) => it?.professional as unknown as string,
      ),
    );
  }, [jobDetail, jobId]);

  const handleClosePopup = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();
      clearError();
      clearSuccess();
      onClosePopup();
      setActiveTab(0);
      setSelectedProfessionals(new Set());
    },
    [clearError, clearSuccess, onClosePopup],
  );

  useEffect(() => {
    if (isOpen && defaultProfessionals) {
      setSelectedProfessionals(new Set(defaultProfessionals));
    }
  }, [isOpen, defaultProfessionals]);

  useEffect(() => {
    if (isError) {
      handleClosePopup();
    }
  }, [handleClosePopup, isError]);

  useEffect(() => {
    if (favoritesListFiltered?.length) {
      setActiveTab(TAB_INDEX.FAVORITES_LIST);
    } else if (recentsListFiltered?.length >= 3) {
      setActiveTab(TAB_INDEX.RECENT_PROFESSIONALS);
    } else if (recommendedListFiltered?.length) {
      setActiveTab(TAB_INDEX.RECOMMENDED_PROS);
    }
  }, [favoritesListFiltered, recentsListFiltered, recommendedListFiltered]);

  const onSelectProfessionals = useCallback((ids: string[], forceAdd: boolean = false) => {
    setSelectedProfessionals((prevSelected) => {
      const newSelected = new Set(prevSelected);

      ids.forEach((id) => {
        if (newSelected.has(id) && !forceAdd) {
          newSelected.delete(id);
        } else {
          if (!newSelected.has(id)) {
            fireProfessionalSelected({
              professionalId: id,
              proCategory: tabToTProCategory[activeTab] || 'Favorite',
            });
          }
          newSelected.add(id);
        }
      });

      return newSelected;
    });
  }, [activeTab]);

  const onSentInvitations = useCallback(() => {
    if (jobIds?.length) {
      inviteProfessionalsToJobsMutation.mutate(
        {
          jobIds,
          professionalIds: [...selectedProfessionals],
          officeId,
        },
        {
          onSuccess: (_data, payload) => {
            payload.jobIds.forEach((id) => {
              queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
            });

            dispatch({ type: HIDE_JOBS_INVITE_POPUP });
            setTimeout(() => {
              dispatch({
                type: SHOW_MESSAGE,
                payload: {
                  title: 'Invite(s) Successfully Sent!',
                  message: createElement(
                    'span',
                    null,
                    'Your shifts were sent to the GoTu community, and invited professionals received alerts for days they’re available.',
                    createElement('br'),
                    'Note: Professionals won’t receive invites for days in which they’re unavailable.',
                  ),
                  isSuccess: true,
                  action: INVITE_PROFESSIONALS_TO_JOBS,
                  buttonText: 'Close',
                },
              });
            }, 500);
          },
        },
      );

      return;
    }

    if (!jobId) {
      return;
    }

    inviteFavoriteToJobMutation.mutate({
      jobId,
      userIds: [...selectedProfessionals],
      officeId,
    });
  }, [
    inviteFavoriteToJobMutation,
    inviteProfessionalsToJobsMutation,
    jobId,
    jobIds,
    selectedProfessionals,
    officeId,
    dispatch,
    queryClient,
  ]);

  return {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup: handleClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange: setActiveTab,
    favoriteProfessionals: favoritesListFiltered,
    recentProfessionals: recentsListFiltered,
    recommendedProfessionals: recommendedListFiltered,
    sentProfessionalIds,
    jobProfession: jobDetail?.profession,
  };
};
